import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { CalendarDays } from 'lucide-react';
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from '../ui/dropdown-menu';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../ui/dialog';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { ChevronDown, Check } from 'lucide-react';
import { apiFetch } from '../../fetchInterceptor';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const PRESET_OPTIONS = [
  { value: 'today', label: 'Today' },
  { value: 'yesterday', label: 'Yesterday' },
  { value: 'last_7_days', label: 'Last 7 Days' },
  { value: 'last_30_days', label: 'Last 30 Days' },
  { value: 'this_week', label: 'This Week' },
  { value: 'last_week', label: 'Last Week' },
  { value: 'this_month', label: 'This Month' },
  { value: 'last_month', label: 'Last Month' },
  { value: 'this_quarter', label: 'This Quarter' },
  { value: 'last_quarter', label: 'Last Quarter' },
  { value: 'year_to_date', label: 'Year to Date' },
  { value: 'last_year', label: 'Last Year' },
  { value: 'custom', label: 'Custom Range' },
];

export const fmtUSD = (v, opts = {}) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0, ...opts })
    .format(v || 0);

export const rangeQuery = (range) => {
  const p = new URLSearchParams({ preset: range.preset });
  if (range.startDate) p.set('start_date', range.startDate);
  if (range.endDate) p.set('end_date', range.endDate);
  return p.toString();
};

export function DateRangeSelect({ value, onChange, disabled = false, ariaLabel = 'Date range' }) {
  const [customOpen, setCustomOpen] = useState(false);
  const [start, setStart] = useState(value.startDate || '');
  const [end, setEnd] = useState(value.endDate || '');
  const invalid = start && end && new Date(start) > new Date(end);

  const label = value.preset === 'custom' && value.startDate
    ? `${value.startDate} – ${value.endDate}`
    : (PRESET_OPTIONS.find((o) => o.value === value.preset)?.label || 'This Month');

  return (
    <>
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <button
            type="button"
            disabled={disabled}
            aria-label={ariaLabel}
            data-testid="date-range-trigger"
            className="inline-flex h-7 max-w-[150px] items-center gap-1.5 rounded-full border border-[#E5EAF0] bg-slate-50/60 px-2.5 text-[11px] font-medium text-[#657085] transition-colors hover:border-slate-300 hover:bg-white focus:outline-none focus-visible:ring-2 focus-visible:ring-[#20B94B]/40 disabled:cursor-not-allowed disabled:opacity-50"
          >
            <span className="truncate">{label}</span>
            <ChevronDown className="h-3.5 w-3.5 shrink-0" />
          </button>
        </DropdownMenuTrigger>
        <DropdownMenuContent align="end" className="max-h-80 w-48 overflow-auto">
          {PRESET_OPTIONS.map((o) => (
            <DropdownMenuItem
              key={o.value}
              data-testid={`range-option-${o.value}`}
              onSelect={() => {
                if (o.value === 'custom') { setCustomOpen(true); return; }
                onChange({ preset: o.value });
              }}
              className="flex items-center justify-between text-sm"
            >
              {o.label}
              {value.preset === o.value && <Check className="h-4 w-4 text-[#20B94B]" />}
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog open={customOpen} onOpenChange={setCustomOpen}>
        <DialogContent className="max-w-md" data-testid="custom-range-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <span className="flex h-9 w-9 items-center justify-center rounded-full bg-[#20B94B]/10">
                <CalendarDays className="h-4 w-4 text-[#118A35]" />
              </span>
              Select a custom range
            </DialogTitle>
            <DialogDescription>Choose the starting and ending dates.</DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 sm:grid-cols-2">
            <label className="space-y-1.5 text-sm">
              <span className="font-medium">Start date</span>
              <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} data-testid="custom-start-input" />
            </label>
            <label className="space-y-1.5 text-sm">
              <span className="font-medium">End date</span>
              <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} data-testid="custom-end-input" />
            </label>
          </div>
          {invalid && <p className="text-sm text-[#EF4444]">The start date cannot be after the end date.</p>}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="outline" onClick={() => setCustomOpen(false)} data-testid="custom-range-cancel">Cancel</Button>
            <Button
              className="bg-[#20B94B] hover:bg-[#118A35]"
              disabled={!start || !end || invalid}
              data-testid="custom-range-apply"
              onClick={() => { onChange({ preset: 'custom', startDate: start, endDate: end }); setCustomOpen(false); }}
            >
              Apply
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}

const RANGE_KEYS = ['revenue', 'net_profit', 'total_cash', 'outstanding_invoices',
  'cash_flow', 'profit_loss', 'top_expenses', 'invoices_overview'];
const LS_KEY = 'cb-dashboard-hub-ranges';
const DEFAULT_RANGE = { preset: 'this_month' };

export function useHubRanges(wsId) {
  const [searchParams, setSearchParams] = useSearchParams();
  const [ranges, setRanges] = useState(() => {
    const out = {};
    let ls = {};
    try { ls = JSON.parse(localStorage.getItem(LS_KEY) || '{}'); } catch { /* noop */ }
    RANGE_KEYS.forEach((k) => {
      const urlPreset = searchParams.get(k);
      if (urlPreset && PRESET_OPTIONS.some((o) => o.value === urlPreset) && urlPreset !== 'custom') {
        out[k] = { preset: urlPreset };
      } else if (ls[k]?.preset) {
        out[k] = ls[k];
      } else {
        out[k] = DEFAULT_RANGE;
      }
    });
    return out;
  });
  const loadedServer = useRef(false);

  useEffect(() => {
    if (!wsId || loadedServer.current) return;
    loadedServer.current = true;
    apiFetch(`${API_URL}/api/dashboard-hub/preferences?workspace_id=${wsId}`)
      .then((r) => (r.ok ? r.json() : null))
      .then((d) => {
        if (!d?.ranges || !Object.keys(d.ranges).length) return;
        // Cross-device sync: server prefs win unless the URL explicitly overrides a key.
        setRanges((cur) => {
          const next = { ...cur };
          Object.entries(d.ranges).forEach(([k, v]) => {
            if (!RANGE_KEYS.includes(k) || !v?.preset) return;
            if (!searchParams.get(k)) next[k] = v;
          });
          localStorage.setItem(LS_KEY, JSON.stringify(next));
          return next;
        });
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [wsId]);

  const update = (key, range) => {
    setRanges((cur) => {
      const next = { ...cur, [key]: range };
      localStorage.setItem(LS_KEY, JSON.stringify(next));
      return next;
    });
    setSearchParams((sp) => { sp.set(key, range.preset); return sp; }, { replace: true });
    if (wsId) {
      apiFetch(`${API_URL}/api/dashboard-hub/preferences?workspace_id=${wsId}`, {
        method: 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ key, range }),
      }).catch(() => {});
    }
  };

  return [ranges, update];
}

export function useHubData(url, deps) {
  const [state, setState] = useState({ data: null, loading: true, error: null });
  const [tick, setTick] = useState(0);
  useEffect(() => {
    if (!url) return undefined;
    let alive = true;
    setState((s) => ({ ...s, loading: true, error: null }));
    apiFetch(`${API_URL}${url}`)
      .then(async (r) => {
        if (!r.ok) throw new Error((await r.json().catch(() => null))?.detail || `Request failed (${r.status})`);
        return r.json();
      })
      .then((d) => alive && setState({ data: d, loading: false, error: null }))
      .catch((e) => alive && setState({ data: null, loading: false, error: e.message }));
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [url, tick, ...(deps || [])]);
  return { ...state, retry: () => setTick((t) => t + 1) };
}

export function CardError({ onRetry }) {
  return (
    <div className="flex flex-col items-center gap-2 py-6 text-center" data-testid="card-error">
      <p className="text-sm text-[#657085]">Couldn't load this data.</p>
      <Button size="sm" variant="outline" onClick={onRetry} data-testid="card-retry-btn">Retry</Button>
    </div>
  );
}

export function CardEmpty({ label = 'No data available for this period' }) {
  return <p className="py-6 text-center text-sm text-[#657085]" data-testid="card-empty">{label}</p>;
}
